import { mutate } from 'swr';
import { ProfileResponse } from 'components/login/types';
import { fetchUrlWithCookies } from 'contexts/user';
import {
  UpdateProfileResponse,
  UpdateProfileError,
  UpdateProfileErrorField,
} from 'types/profile';
import { getEmbedUrlDetailUrl, getEmbedUrlIsActiveUrl } from '../embedUrls';
import { EmbedUrl } from 'types/embedUrls';
import { EnterpriseFeatureValue } from 'types/team';

/**
 * Updates users password
 */
export const updatePassword = async (newPassword: string) => {
  const response = await fetchUrlWithCookies('/api/profile', {
    method: 'PUT',
    body: JSON.stringify({ password: newPassword }),
  });
  return response.status === 200 || response.status === 302;
};

/**
 * Updates user profile, or a team profile if provided with a team id
 */
export const updateProfile = async (
  profile: ProfileResponse,
  teamId: number | null = null
): Promise<UpdateProfileResponse> => {
  const endpoint = teamId ? `/api/teams/${teamId}` : '/api/profile';

  // TODO: fix this discrepancy on the backend
  const updatedProfile: any = { ...profile };
  updatedProfile.blurb = updatedProfile.bio;
  delete updatedProfile.bio;

  const response = await fetchUrlWithCookies(endpoint, {
    method: 'PUT',
    body: JSON.stringify(updatedProfile),
  });

  if (response.status >= 400) {
    const errors = (await response.json()) as Record<
      UpdateProfileErrorField,
      UpdateProfileError
    >;
    return { errors };
  }
  mutate(endpoint);
  return {};
};

/**
 * Uploads image and provides an avatar url.
 * Uploads user avatar, or a team avatar if provided with an team id
 */
export const fetchAvatarUrl = async (
  arrayBuffer: string | ArrayBuffer | null,
  teamId?: number
) => {
  const endpoint = teamId ? `/api/teams/${teamId}/avatar` : '/api/avatar';
  const res = await fetchUrlWithCookies(endpoint, {
    method: 'POST',
    body: arrayBuffer,
  });
  if (teamId) {
    mutate(`/api/teams/${teamId}`);
  }
  const body = await res.json();
  return body.url as string;
};

/**
 * Deletes a user account, or a team if provided with an id
 */
export const deleteAccount = async (
  password: string,
  teamId: string | number | null = null
) => {
  const endpoint = teamId ? `/api/teams/${teamId}` : '/api/me';
  const response = await fetchUrlWithCookies(endpoint, {
    method: 'DELETE',
    body: JSON.stringify({
      data: {
        password: password,
      },
    }),
  });
  const error = await response.text();
  const success = response.status === 200 || response.status === 302;
  return { success, error };
};

export const resendVerificationToken = async () => {
  const response = await fetchUrlWithCookies('/api/resend_verification', {
    method: 'POST',
  });
  const success = response.status === 204;

  return { success };
};

export const acceptTeamInvite = async (teamOwnerId: number) => {
  const response = await fetchUrlWithCookies(
    `/api/teams/${teamOwnerId}/invite/accept`,
    {
      method: 'POST',
    }
  );
  const success = response.status === 200;
  mutate('/api/notifications');
  return { success };
};

export const rejectTeamInvite = async (teamOwnerId: number) => {
  const response = await fetchUrlWithCookies(
    `/api/teams/${teamOwnerId}/invite/reject`,
    {
      method: 'POST',
    }
  );
  const success = response.status === 200;
  mutate('/api/notifications');
  return { success };
};

export const markNotificationsRead = async () => {
  const response = await fetchUrlWithCookies('/api/notifications/read', {
    method: 'POST',
  });
  const success = response.status === 200;
  return { success };
};

export const fetchEmbedUrlDetails = async (
  shareLinkId: string
): Promise<EmbedUrl | undefined> => {
  const url = getEmbedUrlDetailUrl(shareLinkId);
  const response = await fetchUrlWithCookies(url);

  if (response.status !== 200) {
    return undefined;
  }

  const data = await response.json();
  return EmbedUrl.fromAPI(data);
};

export const setEmbedUrlIsActive = async (
  shareLinkId: string,
  isActive: boolean
): Promise<void> => {
  const url = getEmbedUrlIsActiveUrl(shareLinkId);

  const response = await fetchUrlWithCookies(url, {
    method: 'PUT',
    body: JSON.stringify({ isActive }),
  });

  if (response.status !== 200) {
    return undefined;
  }
};

/** Updates Enterprise Settings. */
export const saveEnterpriseSettings = async (
  teamId: number,
  settings: EnterpriseFeatureValue[]
): Promise<EnterpriseFeatureValue[] | undefined> => {
  const response = await fetchUrlWithCookies(`/features/${teamId}`, {
    method: 'POST',
    body: JSON.stringify(settings),
  });

  if (response.status !== 200) {
    return undefined;
  }

  const data = await response.json();
  return data;
};
